import { LibraryVideo, VideosModel, VideoType } from './videos.domain';
import { Actions, Async, F1, Option } from '@client/new/helpers/types';
import { EditParams, useVideosModel } from './videos.model';
import { makeAsync } from '@client/new/helpers/async';
import { useEffect, useState } from 'react';
import { sorting, Sort } from '@client/new/helpers/sorting';
import { UseVideosParams } from './videos.hooks';
import { toRow } from './videos.viewModel.list';
import { TableListRow } from './components/TableList';
import { Control, FieldErrors, useForm } from 'react-hook-form';
import { z } from 'zod';
import _ from 'lodash';
import { muiForm } from '@client/new/helpers/forms';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';

type VideosSortKeys = keyof Pick<
  LibraryVideo,
  'name' | 'createdAt' | 'fileSize'
>;

type Step = 'trim' | 'thumbnail';

export type InitialForm = {
  name?: string;
  description?: string;
  thumbnailTimestamp?: number;
};

export type VideosViewModel = Pick<VideosModel, 'refetchStatus' | 'media'> & {
  searchText: string;
  dispatch: VideosVMDispatcher;
  rows: Array<TableListRow>;
  sort: Sort<VideosSortKeys>;
  sortOptions: Array<Option<VideosSortKeys>>;
  showEditMedia: boolean;
  step: Step;
  editedVideo: LibraryVideo | null;
  influencerId: number;
  submittingEdit: boolean;
  control: Control<InitialForm, any, InitialForm>;
  editErrors: FieldErrors<InitialForm>;
  showChangeThumbnail: boolean;
};

export type VideosVMActionsMap = {
  searchText: string;
  sort: Sort<VideosSortKeys>;
  editMedia: boolean;
  cancelEdit: never;
  setVideoParams: { file: File; editParams?: EditParams };
  setStep: Step;
  setThumbnail: number;

  editVideo: LibraryVideo;
  deleteVideo: { videoId: string };
  submitEdit: null;
  backEdit: null;
  thumbnailTimestamp: number;
  changeThumbnail: null;
  restoreThumbnail: null;
};

export type VideosVMActions = Actions<VideosVMActionsMap>;

export type VideosVMDispatcher = F1<VideosVMActions, null>;

type VideosViewModelState = Pick<
  VideosViewModel,
  | 'searchText'
  | 'sort'
  | 'showEditMedia'
  | 'step'
  | 'editedVideo'
  | 'submittingEdit'
  | 'showChangeThumbnail'
>;

const sortOptions: VideosViewModel['sortOptions'] = [
  {
    value: 'name',
    label: 'Name',
  },
  {
    value: 'createdAt',
    label: 'Upload date',
  },
  {
    value: 'fileSize',
    label: 'Size',
  },
];

export type ToVideoListParams = Omit<VideosViewModelState, 'step'> &
  Pick<VideosModel, 'videos'> & { selectedList: VideoType };

export const editVideoForm = z.object({
  name: z.string().nonempty('Field is required'),
  description: z.string().optional(),
  thumbnailTimestamp: z.number().optional(),
});

export const toVideosList = ({
  searchText,
  videos,
  sort,
}: ToVideoListParams): LibraryVideo[] => {
  const searchTerm = searchText.toLocaleLowerCase();
  const filtered = searchText
    ? videos.filter((v) => v.name.toLocaleLowerCase().includes(searchTerm))
    : videos;
  const sorted = sort ? sorting(sort, filtered) : filtered;
  return sorted;
};

const mapValuesToPayload = (values: InitialForm, defaultValues?: any) => {
  const variables = _.omitBy(values, _.isUndefined);

  if (!defaultValues) return variables;

  const payload: Record<string, string | number> = {};
  for (const prop in variables) {
    if (defaultValues[prop] !== variables[prop]) {
      payload[prop] = variables[prop];
    }
  }
  return payload;
};

export const useVideosViewModel = (
  p: UseVideosParams,
): Async<VideosViewModel> => {
  const asyncModel = useVideosModel(p);
  const [viewModelState, setViewModelState] = useState<VideosViewModelState>({
    searchText: '',
    sort: { type: 'desc', field: 'createdAt' },
    showEditMedia: false,
    editedVideo: null,
    step: 'trim',
    submittingEdit: false,
    showChangeThumbnail: false,
  });

  const { handleSubmit, setValue, control, formState } = muiForm(
    useForm<InitialForm, any, InitialForm>({
      defaultValues: {
        name: undefined,
        description: undefined,
        thumbnailTimestamp: 0,
      },
      resolver: zodResolver(editVideoForm),
    }),
  );

  const toRows = () => {
    if (asyncModel.type !== 'fetched') return [];
    const list = model.videos;
    const { searchText } = viewModelState;
    const searchTerm = searchText.toLocaleLowerCase();
    const filtered = searchText
      ? list.filter((v) => v.name.toLocaleLowerCase().includes(searchTerm))
      : list;
    const sorted = viewModelState.sort
      ? sorting(viewModelState.sort, filtered)
      : filtered;
    return sorted
      .map((v) => toRow(v, dispatch))
      .filter(Boolean) as TableListRow[];
  };

  useEffect(() => {
    setValue('name', viewModelState.editedVideo?.name || undefined);
    setValue(
      'description',
      viewModelState.editedVideo?.description || undefined,
    );
    setValue('thumbnailTimestamp', 0);
  }, [setValue, viewModelState.editedVideo]);

  if (asyncModel.type !== 'fetched') return asyncModel;
  const model = asyncModel.value;

  const dispatch: VideosVMDispatcher = (action) => {
    switch (action.type) {
      case 'setStep':
        setViewModelState({ ...viewModelState, step: action.payload });
        return null;

      case 'searchText':
        setViewModelState({ ...viewModelState, searchText: action.payload });
        return null;
      case 'sort':
        setViewModelState({ ...viewModelState, sort: action.payload });
        return null;
      case 'setVideoParams':
        model.dispatch({
          type: 'setVideoParams',
          payload: action.payload,
        });
        setViewModelState({ ...viewModelState, step: 'thumbnail' });
        return null;
      case 'setThumbnail':
        model.dispatch({
          type: 'setThumbnail',
          payload: action.payload,
        });
        return null;

      case 'editMedia':
        setViewModelState({ ...viewModelState, showEditMedia: action.payload });
        return null;
      case 'editVideo':
        setViewModelState({ ...viewModelState, editedVideo: action.payload });
        return null;
      case 'deleteVideo':
        console.log('will remove video vid:' + action.payload.videoId);
        return null;
      case 'cancelEdit':
        setViewModelState({
          ...viewModelState,
          showEditMedia: false,
          editedVideo: null,
          step: 'trim',
          showChangeThumbnail: false,
        });
        model.dispatch({ type: 'cancelEdit' });
        return null;
      case 'submitEdit':
        onSubmitEdit();
        return null;
      case 'backEdit':
        setViewModelState({ ...viewModelState, showChangeThumbnail: false });
        return null;
      case 'changeThumbnail':
        setViewModelState({ ...viewModelState, showChangeThumbnail: true });
        return null;
      case 'thumbnailTimestamp':
        setValue('thumbnailTimestamp', action.payload);
        toast.success('Thumbnail changed!');
        return null;
      case 'restoreThumbnail':
        setValue('thumbnailTimestamp', undefined);
        toast.success('Thumbnail restored!');
        return null;
    }
  };

  const onSubmitEdit = handleSubmit(async (values) => {
    setViewModelState({ ...viewModelState, submittingEdit: true });
    try {
      const payload = mapValuesToPayload(values, {
        name: viewModelState.editedVideo?.name,
        description: viewModelState.editedVideo?.description,
        thumbnailTimestamp: 0,
      });

      if (_.isEmpty(payload)) {
        toast.custom('No changes to be saved!');
        return;
      }
      return model
        .editVideo({
          variables: {
            creatorId: p.influencerId,
            id: viewModelState.editedVideo!.id,
            ...payload,
          },
        })
        .then(() => {
          toast.success('Video updated successfully!');
        });
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      setViewModelState({ ...viewModelState, submittingEdit: false });
    }
  });

  return makeAsync.fetched<VideosViewModel>({
    rows: toRows(),
    dispatch,
    sortOptions,
    refetchStatus: model.refetchStatus,
    ...viewModelState,

    media: model.media,
    influencerId: p.influencerId,
    control,
    editErrors: formState['errors'],
  });
};
